import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle, Clock, CreditCard, Bot, AlertTriangle, ArrowUpCircle } from "lucide-react";
import { format } from "date-fns";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PhoneCall } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { signOut } from "@/lib/firebase";
import { useLanguage } from "@/contexts/LanguageContext";

interface Restaurant {
  id: string;
  subscriptionStatus?: string;
  trialEndsAt?: Date | string | null;
  currentPeriodEnd?: Date | string | null;
}

interface SubscriptionStatusProps {
  restaurant: Restaurant;
}

export function SubscriptionStatus({ restaurant }: SubscriptionStatusProps) {
  const { toast } = useToast();
  const { formatCurrency } = useLanguage();
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  const isTrialing = restaurant.subscriptionStatus === 'trialing';
  const isActive = restaurant.subscriptionStatus === 'active';
  const trialEndDate = restaurant.trialEndsAt ? new Date(restaurant.trialEndsAt) : null;
  const periodEndDate = restaurant.currentPeriodEnd ? new Date(restaurant.currentPeriodEnd) : null;
  const [usageView, setUsageView] = useState<'month' | 'all'>('month');

  // Fetch subscription status including usage data
  const { data: subscriptionData } = useQuery<{
    subscriptionStatus: string;
    subscriptionTier?: string;
    aiUsageCount: number;
    currentMonthUsage: number;
    aiUsageSeconds?: number;
    currentMonthUsageSeconds?: number;
    receptionistUsageSeconds?: number;
    currentMonthReceptionistUsageSeconds?: number;
  }>({
    queryKey: ['/api/subscription/status'],
    enabled: isTrialing || isActive,
  });

  // Get tier info
  const tier = subscriptionData?.subscriptionTier;
  const tierName = tier ? (tier.charAt(0).toUpperCase() + tier.slice(1)) : '';
  const tierPricing = tier ? ({
    basic: '$149.99',
    growth: '$249.99',
    pro: '$449.99',
  }[tier as string] || '$149.99') : '';

  const totalSeconds = subscriptionData?.aiUsageSeconds || 0;
  const monthSeconds = subscriptionData?.currentMonthUsageSeconds || 0;

  // Calculate costs ($0.0483 per second to match Stripe's per-second billing)
  const COST_PER_SECOND = 0.00483;
  

  // Helper to format seconds to mm:ss
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins === 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  // Cancel subscription mutation
  const cancelSubscriptionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/subscription/cancel');
      return response.json();
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['/api/restaurant'] });
      queryClient.invalidateQueries({ queryKey: ['/api/subscription/status'] });
      toast({
        title: "Subscription Cancelled",
        description: "Your subscription has been cancelled and your account will be deleted.",
      });
      // Sign out from Firebase
      try {
        await signOut();
      } catch (error) {
        console.error("Error signing out:", error);
      }
      // Redirect to home after a brief delay
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <Card className="p-6">
      <div className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold">Subscription</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Your current subscription status
          </p>
        </div>

        {isTrialing && (
          <div className="flex items-center gap-3 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
            <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <div className="flex-1">
              <p className="font-medium text-blue-900 dark:text-blue-100">
                Trial Period - {tierName} Plan
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                {tierPricing}/month + AI usage fees
                {trialEndDate && ` • Trial ends ${format(trialEndDate, 'MMMM d, yyyy')}`}
              </p>
            </div>
          </div>
        )}

        {isActive && (
          <div className="flex items-center gap-3 p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
            <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            <div className="flex-1">
              <p className="font-medium text-green-900 dark:text-green-100">
                Active Subscription - {tierName} Plan
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                {tierPricing}/month + AI usage
                {periodEndDate && ` • Renews on ${format(periodEndDate, 'MMMM d, yyyy')}`}
              </p>
            </div>
          </div>
        )}

        {!isTrialing && !isActive && (
          <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-950 rounded-lg border border-amber-200 dark:border-amber-800">
            <CreditCard className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <div className="flex-1">
              <p className="font-medium text-amber-900 dark:text-amber-100">Subscription Required</p>
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Subscribe to continue accepting orders • Starting at $149.99/month
              </p>
            </div>
            <Link href="/subscribe">
              <Button size="sm" data-testid="button-subscribe">
                Subscribe
              </Button>
            </Link>
          </div>
        )}

        {(isTrialing || isActive) && (
          <>
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">AI Performance Tracking</h4>
                </div>
                <Tabs value={usageView} onValueChange={(v) => setUsageView(v as 'month' | 'all')} className="w-[200px]">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="month" className="text-xs">This Month</TabsTrigger>
                    <TabsTrigger value="all" className="text-xs">All Time</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* AI Waiter Usage */}
                <div className="p-4 rounded-xl border bg-card/50 backdrop-blur-sm relative overflow-hidden group">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm font-medium">AI Waiter Usage</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-3xl font-bold tracking-tight">
                      {formatDuration(usageView === 'month' ? (subscriptionData?.currentMonthUsageSeconds || 0) : (subscriptionData?.aiUsageSeconds || 0))}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-muted-foreground">$0.00483/sec</p>
                      <p className="text-base font-semibold text-primary">
                        {formatCurrency((usageView === 'month' ? (subscriptionData?.currentMonthUsageSeconds || 0) : (subscriptionData?.aiUsageSeconds || 0)) * 0.00483)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI Receptionist Usage */}
                <div className="p-4 rounded-xl border bg-card/50 backdrop-blur-sm relative overflow-hidden group">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-sm font-medium">AI Receptionist Usage</span>
                  </div>
                  <div className="space-y-1">
                    <p className="text-3xl font-bold tracking-tight">
                      {formatDuration(usageView === 'month' ? (subscriptionData?.currentMonthReceptionistUsageSeconds || 0) : (subscriptionData?.receptionistUsageSeconds || 0))}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-muted-foreground">$0.00483/sec</p>
                      <p className="text-base font-semibold text-indigo-500">
                        {formatCurrency((usageView === 'month' ? (subscriptionData?.currentMonthReceptionistUsageSeconds || 0) : (subscriptionData?.receptionistUsageSeconds || 0)) * 0.00483)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 flex flex-wrap items-center gap-3">
              <Link href="/subscribe">
                <Button
                  size="sm"
                  data-testid="button-upgrade-plan"
                >
                  <ArrowUpCircle className="h-4 w-4 mr-2" />
                  Upgrade Plan
                </Button>
              </Link>

              {subscriptionData?.subscriptionStatus === 'active' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const response = await apiRequest('POST', '/api/subscription/portal');
                      const data = await response.json();
                      if (data.url) window.location.href = data.url;
                    } catch (error) {
                      toast({
                        title: "Error",
                        description: "Failed to open billing portal",
                        variant: "destructive",
                      });
                    }
                  }}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Manage Billing
                </Button>
              )}

              <Button
                variant="destructive"
                size="sm"
                onClick={() => setShowCancelDialog(true)}
                disabled={cancelSubscriptionMutation.isPending}
                data-testid="button-cancel-subscription"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Cancel Subscription
              </Button>
              <p className="text-xs text-muted-foreground w-full mt-1">
                Cancelling your subscription will delete your restaurant and all data
              </p>
            </div>
          </>
        )}
      </div>

      <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel Subscription & Delete Account?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Cancelling your subscription will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Immediately cancel your Stripe subscription</li>
                <li>Delete your restaurant and all menu items</li>
                <li>Delete all orders and customer data</li>
                <li>Remove all QR codes and analytics</li>
              </ul>
              <p className="mt-3 font-semibold">
                Are you sure you want to proceed?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-dialog-no">
              Keep Subscription
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => cancelSubscriptionMutation.mutate()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="button-cancel-dialog-yes"
            >
              Yes, Cancel & Delete Everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
